import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Award, Plus, Trash2, Star, Edit2, Eye, Check } from "lucide-react";
import api from "../../lib/api";


const layouts = [
  { value: "classic", label: "Classic", desc: "Traditional ornate border with gold accents" },
  { value: "modern", label: "Modern", desc: "Clean design with bold typography" },
  { value: "minimal", label: "Minimal", desc: "Simple and elegant with minimal decoration" },
];

const CertificateTemplatesPage = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", layout: "classic", primary_color: "#1B4F8A", accent_color: "#D4AF37",
    show_logo: true, show_signature: true, signature_name: "", signature_title: "",
    custom_text: "", border_style: "ornate"
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/certificate-templates");
      setTemplates(res.data.templates || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      if (editing) {
        await api.patch("/certificate-templates/${editing}", form);
        toast.success("Template updated");
      } else {
        await api.post("/certificate-templates", form);
        toast.success("Template created");
      }
      setShowCreate(false);
      setEditing(null);
      setForm({ name: "", layout: "classic", primary_color: "#1B4F8A", accent_color: "#D4AF37", show_logo: true, show_signature: true, signature_name: "", signature_title: "", custom_text: "", border_style: "ornate" });
      fetchData();
    } catch (e) { toast.error("Failed to save"); }
  };

  const startEdit = (tmpl) => {
    setForm({ ...tmpl });
    setEditing(tmpl.id);
    setShowCreate(true);
  };

  const setDefault = async (id) => {
    try {
      await api.patch("/certificate-templates/${id}", { is_default: true });
      toast.success("Default template updated");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteTmpl = async (id) => {
    try {
      await api.delete("/certificate-templates/${id}");
      toast.success("Template deleted");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Certificate Templates">
    <div className="p-6 space-y-6" data-testid="certificate-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Certificate Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Customize certificate designs for your organization</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: "", layout: "classic", primary_color: "#1B4F8A", accent_color: "#D4AF37", show_logo: true, show_signature: true, signature_name: "", signature_title: "", custom_text: "", border_style: "ornate" }); setShowCreate(true); }} className="btn-primary" data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Create/Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center overflow-y-auto p-4" onClick={() => { setShowCreate(false); setEditing(null); }}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-2xl p-6 space-y-4 my-8" onClick={e => e.stopPropagation()} data-testid="cert-template-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>{editing ? "Edit" : "Create"} Certificate Template</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><label className="text-sm font-medium">Template Name</label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Safety Training Certificate" className="mt-1" data-testid="cert-name-input" /></div>
                <div><label className="text-sm font-medium">Layout</label>
                  <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.layout} onChange={e => setForm({...form, layout: e.target.value})}>
                    {layouts.map(l => <option key={l.value} value={l.value}>{l.label} - {l.desc}</option>)}
                  </select></div>
                <div><label className="text-sm font-medium">Border Style</label>
                  <select className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-background" value={form.border_style} onChange={e => setForm({...form, border_style: e.target.value})}>
                    <option value="ornate">Ornate</option><option value="simple">Simple</option><option value="none">None</option>
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-medium">Primary Color</label><input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="w-full h-9 mt-1 rounded-md border cursor-pointer" /></div>
                  <div><label className="text-sm font-medium">Accent Color</label><input type="color" value={form.accent_color} onChange={e => setForm({...form, accent_color: e.target.value})} className="w-full h-9 mt-1 rounded-md border cursor-pointer" /></div>
                </div>
                <div><label className="text-sm font-medium">Signature Name</label>
                  <Input value={form.signature_name} onChange={e => setForm({...form, signature_name: e.target.value})} placeholder="John Smith" className="mt-1" /></div>
                <div><label className="text-sm font-medium">Signature Title</label>
                  <Input value={form.signature_title} onChange={e => setForm({...form, signature_title: e.target.value})} placeholder="Director of Training" className="mt-1" /></div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_logo} onChange={e => setForm({...form, show_logo: e.target.checked})} /> Show Logo</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_signature} onChange={e => setForm({...form, show_signature: e.target.checked})} /> Show Signature</label>
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="text-sm font-medium mb-2 block">Preview</label>
                <div className="border-2 rounded-lg p-6 aspect-[1.4] flex flex-col items-center justify-center text-center" style={{
                  borderColor: form.border_style === "none" ? "#e5e7eb" : form.accent_color,
                  borderStyle: form.border_style === "ornate" ? "double" : "solid",
                  borderWidth: form.border_style === "ornate" ? "6px" : form.border_style === "simple" ? "2px" : "1px",
                  background: `linear-gradient(135deg, ${form.primary_color}08, white, ${form.accent_color}08)`,
                }}>
                  {form.show_logo && <div className="w-8 h-8 rounded-full mb-2" style={{ background: form.primary_color }} />}
                  <p className="text-[9px] uppercase tracking-widest" style={{ color: form.primary_color }}>Certificate of Completion</p>
                  <p className="text-sm font-bold mt-1" style={{ color: form.primary_color }}>Learner Name</p>
                  <p className="text-[8px] text-muted-foreground mt-1">Has successfully completed</p>
                  <p className="text-[10px] font-semibold" style={{ color: form.accent_color }}>Course Title</p>
                  {form.show_signature && (
                    <div className="mt-3 pt-2 border-t border-dashed w-24">
                      <p className="text-[7px]" style={{ color: form.primary_color }}>{form.signature_name || "Signature"}</p>
                      <p className="text-[6px] text-muted-foreground">{form.signature_title || "Title"}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); }}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSave} data-testid="save-cert-template-btn">{editing ? "Update" : "Create"} Template</Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(tmpl => (
          <div key={tmpl.id} className="border rounded-xl overflow-hidden bg-background shadow-sm" data-testid={`cert-template-${tmpl.id}`}>
            {/* Mini Preview */}
            <div className="h-40 flex items-center justify-center p-4" style={{
              borderBottom: `3px solid ${tmpl.accent_color || '#D4AF37'}`,
              background: `linear-gradient(135deg, ${tmpl.primary_color || '#1B4F8A'}10, white, ${tmpl.accent_color || '#D4AF37'}10)`,
            }}>
              <div className="text-center">
                <Award className="w-8 h-8 mx-auto mb-1" style={{ color: tmpl.accent_color || '#D4AF37' }} />
                <p className="text-[10px] uppercase tracking-widest" style={{ color: tmpl.primary_color }}>Certificate of Completion</p>
                <p className="text-xs font-bold mt-1" style={{ color: tmpl.primary_color }}>Learner Name</p>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-sm">{tmpl.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground capitalize">{tmpl.layout}</span>
                    {tmpl.is_default && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800">DEFAULT</span>}
                    {tmpl.tenant_id && <span className="text-[10px] text-muted-foreground">Tenant-specific</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 mt-3">
                <Button variant="ghost" size="sm" className="h-8 flex-1" onClick={() => startEdit(tmpl)} data-testid={`edit-cert-${tmpl.id}`}><Edit2 className="w-3.5 h-3.5 mr-1" />Edit</Button>
                {!tmpl.is_default && <Button variant="ghost" size="sm" className="h-8" onClick={() => setDefault(tmpl.id)}><Star className="w-3.5 h-3.5" /></Button>}
                {!tmpl.is_default && <Button variant="ghost" size="sm" className="h-8 text-red-600" onClick={() => deleteTmpl(tmpl.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    </AdminLayout>
  );
};

export default CertificateTemplatesPage;
